import React, { useState, useEffect, useMemo } from 'react';
import { chooseChips, divideChipsIntoStacks } from './helpers';
import PokerChipStack from './PokerChipStack';
import { PokerChipValue } from './PokerChipValue';
import { twMerge } from 'tailwind-merge';
import PotAmount from '@/components/PotAmount';
import { motion } from 'framer-motion';
import { BetPosition } from '../UserPanel/UserPanel';
import { COLORS } from '@/constants/COLORS';
import useBBDisplay from '@/hooks/useBBDisplay';

export interface PokerChipStacksProps {
  amount: number | undefined;
  entropy?: number;
  flexDirection?: 'col' | 'row';
  bigBlind: number;
  betPosition?: BetPosition;
  hideCurrencyIcon?: boolean;
  singleChipStack?: boolean;
  shortCurrencyFormat?: boolean;
  mobileCurrencyFormat?: boolean;
  tableId: string;
}

const PokerChipStacks: React.FC<PokerChipStacksProps> = ({
  amount = 0,
  entropy = 0,
  flexDirection = 'row',
  bigBlind,
  betPosition,
  hideCurrencyIcon = false,
  singleChipStack = false,
  shortCurrencyFormat = false,
  mobileCurrencyFormat = false,
  tableId,
}) => {
  const [chips, setChips] = useState<PokerChipValue[]>([]);
  const { handleBBDisplayChange, displayInBBEnabled, shouldDisplayInBB } = useBBDisplay({
    tableId,
  });

  useEffect(() => {
    if (amount > 0) {
      const initialChips = chooseChips(amount, entropy);
      setChips(initialChips);
    }
  }, [amount, entropy]);

  // const stacks = divideChipsIntoStacks(chips);

  const stacks = useMemo(() => divideChipsIntoStacks(chips), [chips]);
  const mobileStacks = useMemo(() => {
    const stackLength = Math.min(3, chips.length);
    return chips.slice(0, stackLength);
  }, [chips]);

  if (!amount || chips.length === 0) {
    return null;
  }

  const onClickContainerHandler = () => {
    handleBBDisplayChange(!displayInBBEnabled);
  };

  if (!amount || chips.length === 0) {
    return null;
  }

  return (
    <div
      onClick={onClickContainerHandler}
      className={twMerge(
        'flex cursor-pointer gap-0',
        flexDirection === 'row'
          ? 'flex-row items-end' // Changed from items-center to items-end
          : 'flex-col items-center gap-1', // Added items-center for vertical alignment
      )}
    >
      <div
        style={{
          direction: betPosition?.includes(BetPosition.Left) ? 'rtl' : 'ltr',
        }}
        className="flex flex-row"
      >
        {singleChipStack ? (
          <motion.div
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              marginLeft: betPosition?.includes(BetPosition.Right) ? '-0.15em' : '',
              marginRight: !betPosition?.includes(BetPosition.Right) ? `-0.15em` : '',
              zIndex: stacks.length,
            }}
          >
            <PokerChipStack chips={mobileStacks} />
          </motion.div>
        ) : (
          stacks.map((stack, index) => (
            <motion.div
              key={index}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                marginLeft: betPosition?.includes(BetPosition.Right) ? '-0.15em' : '',
                marginRight: !betPosition?.includes(BetPosition.Right) ? `-0.15em` : '',
                zIndex: stacks.length - index,
              }}
            >
              <PokerChipStack chips={stack} />
            </motion.div>
          ))
        )}
      </div>

      <span
        className={twMerge(
          'whitespace-nowrap rounded text-base font-semibold text-white',
          flexDirection === 'col' ? 'mt-2' : 'ml-2', // Adjusted margins
        )}
      >
        <PotAmount
          amount={amount}
          bigBlind={bigBlind}
          shouldDisplayInBB={shouldDisplayInBB}
          fill={COLORS.greenCurrency}
          hideCurrencyIcon={hideCurrencyIcon}
          shortCurrencyFormat={shortCurrencyFormat}
          mobileCurrencyFormat={mobileCurrencyFormat}
        />
      </span>
    </div>
  );
};

export default PokerChipStacks;
